<script setup lang="ts">
import { computed, onMounted } from "vue"

import OrgSettingsLayout from "@Modules/teams/Views/OrgSettingsLayout.vue"
import OrganizationOrdersView from "@Modules/shoppingcart/Views/OrganizationOrdersView.vue"
import { useCurrentOrganization } from "@Modules/teams/composables/useCurrentOrganization"
import { XOrdersStore } from "@Modules/shoppingcart/Store/XOrdersStore"
import { XAuthStore } from "@Modules/users/Store/XAuthStore"
import useNotify from "@Composables/useNotify"

//
// Setup
const { currentOrganizationSlug, currentOrganization } = useCurrentOrganization()
const ordersStore = XOrdersStore()
const authStore = XAuthStore()
const { notifyError, notifyInfo } = useNotify()

//
// Computed
const orders = computed(() => ordersStore.orders)
const loading = computed(() => ordersStore.loading)
const locale = computed(() => authStore.locale)

const translatedTransactionQuantity = computed(() => {
  const quantity = orders.value?.length || 0
  return `${quantity} transaction${quantity !== 1 ? "s" : ""}`
})

//
// Methods
async function recoverNow(order: { id: number }): Promise<void> {
  const result = await ordersStore.retryCheckout(order.id)
  if (result) {
    notifyInfo({ text: "Checkout retry initiated" })
  } else {
    notifyError({ text: "Failed to retry checkout" })
  }
}

async function loadData(): Promise<void> {
  if (!currentOrganizationSlug.value) return
  await ordersStore.loadOrdersForOrganization(currentOrganizationSlug.value)
}

//
// Lifecycle
onMounted(() => {
  loadData()
})

//
// Exposed Test Vars
defineExpose({ orders, loading, currentOrganizationSlug, currentOrganization })
</script>

<template>
  <OrgSettingsLayout
    :organization="currentOrganization"
    :can="currentOrganization?.can('manage_subscriptions') || false"
  >
    <OrganizationOrdersView
      :orders="orders"
      :loading="loading"
      :org-slug="currentOrganizationSlug"
      :locale="locale"
      :translated-transaction-quantity="translatedTransactionQuantity"
      :recover-now="recoverNow"
    />
  </OrgSettingsLayout>
</template>
